import { isApiClientError } from "../../api/errors";
import {
  isCryptoProError,
  type CadesProfile,
  type CryptoProAdapter,
} from "../../cryptopro";
import type {
  DocumentWorkflowApi,
  DocumentWorkflowFileVersion,
  DocumentWorkflowSignature,
  DocumentWorkflowSignatureCapabilities,
} from "./api";

/**
 * Причины, по которым подпись не состоялась или недоступна.
 *
 * Каждая называется своим именем: «плагин не найден», «сервер отключил
 * подпись» и «сервер не может проверить» требуют от человека разных действий,
 * и сводить их к одной ошибке нельзя.
 */
export type SigningFailureKind =
  /** Сервер отключил подпись документов. */
  | "capability_disabled"
  | "permission_denied"
  /** В браузере нет плагина КриптоПро либо он не отвечает. */
  | "plugin_unavailable"
  /** В хранилище нет действующего сертификата с закрытым ключом. */
  | "no_certificates"
  | "certificate_not_found"
  | "profile_unsupported"
  | "user_cancelled"
  /** Срок запроса подписи истёк: nonce больше не годится. */
  | "request_expired"
  /** Сервер не принял подпись как верную. */
  | "signature_invalid"
  /** Серверная проверка сейчас недоступна (HTTP 503). */
  | "verifier_unavailable"
  /** Содержимое версии не получено из хранилища. */
  | "content_unavailable"
  /** Полученные байты не совпали с SHA-256 версии. */
  | "hash_mismatch"
  | "failed";

/** Ошибка самого сценария подписания: до и после обращения к плагину. */
export class SigningFlowError extends Error {
  readonly name = "SigningFlowError";

  constructor(
    readonly kind: SigningFailureKind,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
  }
}

/** Код ошибки Core, когда срок запроса подписи истёк. */
const EXPIRED_CODE = "document_workflow.signature_request_expired";
const INVALID_CODE = "document_workflow.signature_invalid";
const VERIFIER_CODE = "document_workflow.signature_verifier_unavailable";
const DISABLED_CODE = "document_workflow.module_disabled";

/** Свести любую ошибку сценария подписи к одной из названных причин. */
export function classifySigningFailure(error: unknown): SigningFailureKind {
  if (error instanceof SigningFlowError) return error.kind;
  if (isCryptoProError(error)) {
    return error.reason === "signing_failed" ? "failed" : error.reason;
  }
  if (isApiClientError(error)) {
    if (error.code === EXPIRED_CODE) return "request_expired";
    if (error.code === INVALID_CODE) return "signature_invalid";
    if (error.code === VERIFIER_CODE || error.status === 503) {
      return "verifier_unavailable";
    }
    if (error.code === DISABLED_CODE) return "capability_disabled";
    if (error.status === 403) return "permission_denied";
  }

  return "failed";
}

export type SigningAvailability =
  | { readonly status: "available"; readonly profile: CadesProfile }
  | { readonly status: "checking" }
  | {
      readonly status: "unavailable";
      readonly reason:
        | "capabilities_failed"
        | "capability_disabled"
        | "permission_denied"
        | "no_profile"
        | "profile_unsupported"
        | "version_not_ready";
    };

/** Профиль, который разрешён сервером и умеет создавать этот браузер. */
export function pickProfile(
  serverProfiles: readonly CadesProfile[],
  browserProfiles: readonly CadesProfile[],
): CadesProfile | null {
  const common = serverProfiles.filter((profile) =>
    browserProfiles.includes(profile),
  );

  return common.includes("CAdES-BES") ? "CAdES-BES" : (common[0] ?? null);
}

/**
 * Можно ли начинать подписание.
 *
 * Плагин здесь не трогается: пока сервер не ответил, что подпись включена,
 * действие остаётся недоступным.
 */
export function signingAvailability(input: {
  readonly capabilities:
    | { readonly state: "pending" }
    | { readonly state: "error" }
    | {
        readonly state: "ready";
        readonly data: DocumentWorkflowSignatureCapabilities;
      };
  /** `can_sign` документа: подсказка интерфейсу, а не граница доступа. */
  readonly canSign: boolean;
  readonly browserProfiles: readonly CadesProfile[];
  readonly version: Pick<DocumentWorkflowFileVersion, "scan_status" | "sha256">;
}): SigningAvailability {
  const { capabilities } = input;
  if (capabilities.state === "pending") return { status: "checking" };
  if (capabilities.state === "error") {
    return { status: "unavailable", reason: "capabilities_failed" };
  }
  if (!capabilities.data.enabled) {
    return { status: "unavailable", reason: "capability_disabled" };
  }
  if (!input.canSign) {
    return { status: "unavailable", reason: "permission_denied" };
  }
  if (input.version.scan_status !== "clean" || input.version.sha256 === null) {
    return { status: "unavailable", reason: "version_not_ready" };
  }
  if (capabilities.data.profiles.length === 0) {
    return { status: "unavailable", reason: "no_profile" };
  }
  const profile = pickProfile(
    capabilities.data.profiles,
    input.browserProfiles,
  );

  return profile === null
    ? { status: "unavailable", reason: "profile_unsupported" }
    : { status: "available", profile };
}

/** Подпись, готовая к отправке: живёт только в памяти этой вкладки. */
export interface PreparedSignature {
  readonly versionId: string;
  readonly requestId: string;
  readonly nonce: string;
  readonly detachedSignature: string;
  readonly clientVersion: string | null;
  /** Момент, после которого запрос подписи считается устаревшим, мс. */
  readonly expiresAtMs: number;
}

export interface SigningDependencies {
  readonly api: Pick<
    DocumentWorkflowApi,
    "createSignatureRequest" | "downloadVersion" | "completeSignatureRequest"
  >;
  readonly cryptoPro: CryptoProAdapter;
  /** Байты версии по короткоживущей подписанной ссылке. */
  readonly fetchContent?: (url: string) => Promise<ArrayBuffer>;
  readonly sha256Hex?: (content: ArrayBuffer) => Promise<string>;
  readonly now?: () => number;
}

async function fetchSignedContent(url: string): Promise<ArrayBuffer> {
  // Ссылка подписана сама: сессионные cookie в хранилище не нужны и не уходят.
  const response = await fetch(url, { credentials: "omit" });
  if (!response.ok) {
    throw new SigningFlowError(
      "content_unavailable",
      "Хранилище не отдало содержимое версии.",
    );
  }

  return response.arrayBuffer();
}

async function digestHex(content: ArrayBuffer): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", content);

  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Создать подпись версии: запрос → содержимое → проверка хеша → плагин.
 *
 * Запрос подписи создаётся непосредственно перед подписанием, а не заранее:
 * срок его жизни короткий, и просроченный nonce переиспользовать нельзя.
 * Закрытый ключ остаётся в плагине; сюда возвращается только готовая подпись.
 */
export async function prepareSignature(
  deps: SigningDependencies,
  input: {
    readonly version: Pick<DocumentWorkflowFileVersion, "id" | "sha256">;
    readonly certificateId: string;
    readonly profile: CadesProfile;
    readonly requestTtlSeconds: number;
    readonly clientVersion: string | null;
    readonly onStep?: (step: "request" | "content" | "sign") => void;
  },
): Promise<PreparedSignature> {
  const now = deps.now ?? Date.now;
  const fetchContent = deps.fetchContent ?? fetchSignedContent;
  const sha256Hex = deps.sha256Hex ?? digestHex;

  input.onStep?.("request");
  // Отсчёт срока — до ответа сервера: локальный срок не длиннее серверного.
  const startedAt = now();
  const request = await deps.api.createSignatureRequest(
    input.version.id,
    input.profile,
  );
  if (request.nonce === undefined) {
    throw new SigningFlowError(
      "failed",
      "Сервер не выдал одноразовое значение для подписи.",
    );
  }

  input.onStep?.("content");
  let content: ArrayBuffer;
  try {
    const signed = await deps.api.downloadVersion(input.version.id);
    content = await fetchContent(signed.url);
  } catch (error) {
    if (error instanceof SigningFlowError) throw error;
    throw new SigningFlowError(
      "content_unavailable",
      "Не удалось получить содержимое версии для подписи.",
      { cause: error },
    );
  }
  // Подписывается ровно та неизменяемая версия, на которую выдан запрос.
  const actual = (await sha256Hex(content)).toLowerCase();
  if (
    actual !== request.expected_sha256.toLowerCase() ||
    (input.version.sha256 !== null &&
      actual !== input.version.sha256.toLowerCase())
  ) {
    throw new SigningFlowError(
      "hash_mismatch",
      "Содержимое версии не совпало с её SHA-256.",
    );
  }

  input.onStep?.("sign");
  const detachedSignature = await deps.cryptoPro.signDetached({
    certificateId: input.certificateId,
    content,
    profile: input.profile,
  });

  return {
    versionId: input.version.id,
    requestId: request.id,
    nonce: request.nonce,
    detachedSignature,
    clientVersion: input.clientVersion,
    expiresAtMs: startedAt + input.requestTtlSeconds * 1000,
  };
}

/** Устарел ли запрос подписи на этой вкладке. */
export function isSignatureExpired(
  prepared: Pick<PreparedSignature, "expiresAtMs">,
  now: () => number = Date.now,
): boolean {
  return now() >= prepared.expiresAtMs;
}

/**
 * Отправить готовую подпись: сервер сверяет nonce, хеш и проверяет подпись.
 *
 * Устаревший запрос не отправляется: его nonce уже недействителен.
 */
export async function submitSignature(
  deps: Pick<SigningDependencies, "api" | "now">,
  prepared: PreparedSignature,
): Promise<DocumentWorkflowSignature> {
  if (isSignatureExpired(prepared, deps.now)) {
    throw new SigningFlowError(
      "request_expired",
      "Срок запроса подписи истёк.",
    );
  }

  return deps.api.completeSignatureRequest(prepared.requestId, {
    nonce: prepared.nonce,
    detached_signature: prepared.detachedSignature,
    client_version: prepared.clientVersion,
  });
}
